import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type InvoiceDetail = {
  id: number;
  entityId: number;
  competenceYear: number;
  competenceMonth: number;
  statementEnd: string;
  dueDate: string;
  status: string;
  totalAmount: number;
  card: {
    id: number;
    name: string;
    brand?: string | null;
    last4?: string | null;
  };
};

type Transaction = {
  id: number;
  description: string;
  amount: number;
  occurredAt: string;
  installmentNumber?: number | null;
  installmentCount?: number | null;
  origem?: string | null;
};
type Option = { id: number; label: string };

type PdfTransaction = {
  date: string;
  description: string;
  amount: number;
  type: "national" | "international";
  lineCategory: "purchase" | "payment";
  status: "matched" | "missing" | "moved" | "other_invoice" | "payment";
  fromInvoice?: { year: number; month: number };
  otherInvoice?: { id: number; year: number; month: number };
};

type PdfParseResult = {
  transactions: PdfTransaction[];
  stats: {
    total: number;
    matched: number;
    missing: number;
    moved: number;
    otherInvoice: number;
    payment: number;
  };
};

const resetForm = {
  description: "",
  amount: "",
  occurredAt: "",
  installmentCount: "",
  isRecurring: false,
};

export const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams();
  const { client } = useAuth();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [clearingMeta, setClearingMeta] = useState(false);

  // modal state
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [installmentCount, setInstallmentCount] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [categoryOption, setCategoryOption] = useState<Option | null>(null);
  const [costCenterOption, setCostCenterOption] = useState<Option | null>(null);
  const [accountOption, setAccountOption] = useState<Option | null>(null);
  const [supplierOption, setSupplierOption] = useState<Option | null>(null);

  // PDF reconciliation state
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfPassword, setPdfPassword] = useState("");
  const [pdfResult, setPdfResult] = useState<PdfParseResult | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [importingPdf, setImportingPdf] = useState(false);
  const [showPdfSection, setShowPdfSection] = useState(false);
  const [bankAccountId, setBankAccountId] = useState<number | "">("");
  const [bankAccounts, setBankAccounts] = useState<Option[]>([]);
  const [particularPartnerId, setParticularPartnerId] = useState<number | "">("");
  const [partners, setPartners] = useState<Option[]>([]);
  const [particularRows, setParticularRows] = useState<Set<number>>(new Set());
  const [creditRows, setCreditRows] = useState<Set<number>>(new Set());

  const invoiceId = Number(id);
  const entityId = invoice?.entityId ?? 0;

  const {
    rows,
    total,
    loading,
    error: listError,
    page,
    take,
    setPage,
    reload,
  } = usePagedQuery<Transaction>({
    endpoint: `/modules/cards/invoices/${invoiceId}/transactions`,
    initialTake: 50,
  });

  const fetchCategories = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/modules/categories?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as { data: Array<{ id: number; name: string }> };
      return payload.data.map((item) => ({ id: item.id, label: item.name }));
    },
    [client]
  );

  const fetchCostCenters = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/modules/cost-centers?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as { data: Array<{ id: number; name: string; code?: string | null }> };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code ?? ""} ${item.name}`.trim(),
      }));
    },
    [client]
  );

  const fetchAccounts = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as { data: Array<{ id: number; code: string; name: string }> };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  const fetchSuppliers = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/modules/suppliers?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as {
        data: Array<{ id: number; entity: { name: string } }>;
      };
      return payload.data.map((item) => ({ id: item.id, label: item.entity.name }));
    },
    [client]
  );

  useEffect(() => {
    client.get(`/modules/cards/invoices/${invoiceId}`).then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as { data: InvoiceDetail };
      setInvoice(payload.data);
    });
    client.get("/modules/bank-accounts?take=50&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; agency: string; accountNumber: string; bank?: { name: string } | null }>;
      };
      setBankAccounts(
        payload.data.map((item) => ({
          id: item.id,
          label: item.bank
            ? `${item.bank.name} — ag ${item.agency} / cc ${item.accountNumber}`
            : `ag ${item.agency} / cc ${item.accountNumber}`,
        }))
      );
    });
    client.get("/modules/partners?take=50&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as { data: Array<{ id: number; name: string }> };
      setPartners(payload.data.map((item) => ({ id: item.id, label: item.name })));
    });
  }, [client]);

  const toggleParticular = (idx: number) => {
    setParticularRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleCredit = (idx: number) => {
    setCreditRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const canGenerateInstallments = useMemo(
    () => Number(installmentCount) > 1,
    [installmentCount]
  );

  const clearModalFields = () => {
    setDescription(resetForm.description);
    setAmount(resetForm.amount);
    setOccurredAt(resetForm.occurredAt);
    setInstallmentCount(resetForm.installmentCount);
    setIsRecurring(resetForm.isRecurring);
    setCategoryOption(null);
    setCostCenterOption(null);
    setAccountOption(null);
    setSupplierOption(null);
    setModalError(null);
  };

  const openModal = () => { clearModalFields(); setShowModal(true); };
  const closeModal = () => setShowModal(false);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setModalError(null);
    setSaving(true);
    const payload = {
      description,
      amount: parseFloat(String(amount).replace(/\./g, "").replace(",", ".")),
      occurredAt,
      categoryId: categoryOption?.id,
      costCenterId: costCenterOption?.id,
      accountId: accountOption?.id,
      supplierId: supplierOption?.id,
      installmentCount: canGenerateInstallments ? Number(installmentCount) : undefined,
      isRecurring,
    };
    const url = canGenerateInstallments
      ? `/modules/cards/invoices/${invoiceId}/generate-installments`
      : `/modules/cards/invoices/${invoiceId}/transactions`;
    const response = await client.post(url, payload);
    setSaving(false);
    if (!response.ok) {
      const errData = await response.json().catch(() => ({})) as { error?: string };
      setModalError(errData.error ?? "Falha ao salvar. Tente novamente.");
      return;
    }
    closeModal();
    await reload();
  };

  const handleDeleteTransaction = async (txId: number) => {
    if (!confirm("Excluir esta transação?")) return;
    setDeletingId(txId);
    setError(null);
    const response = await client.del(
      `/modules/cards/invoices/${invoiceId}/transactions/${txId}`
    );
    setDeletingId(null);
    if (!response.ok) {
      setError("Falha ao excluir transação.");
      return;
    }
    await reload();
  };

  const handleClearMetaAds = async () => {
    if (!confirm("Limpar todas as transações importadas via Meta Ads desta fatura?")) return;
    setClearingMeta(true);
    setError(null);
    const response = await client.del(`/modules/cards/invoices/${invoiceId}/meta-ads`);
    setClearingMeta(false);
    if (!response.ok) {
      setError("Falha ao limpar transações Meta Ads.");
      return;
    }
    await reload();
  };

  const handleParsePdf = async () => {
    if (!pdfFile) { setPdfError("Selecione um arquivo PDF."); return; }
    setLoadingPdf(true);
    setPdfError(null);
    setPdfResult(null);
    try {
      const token = localStorage.getItem("mpf_admin_access_token");
      const formData = new FormData();
      formData.append("file", pdfFile);
      if (pdfPassword) formData.append("password", pdfPassword);
      const response = await fetch(
        `${import.meta.env.VITE_API_URL as string}/modules/cards/invoices/${invoiceId}/parse-pdf`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: formData,
        }
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error ?? "Falha ao processar PDF.");
      }
      setPdfResult((await response.json()) as PdfParseResult);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : "Falha ao processar PDF.");
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleImportMissing = async () => {
    if (!pdfResult) return;
    const missing = pdfResult.transactions
      .map((t, i) => ({ tx: t, i }))
      .filter(({ tx }) => tx.status === "missing");
    // Also include negative-value "payment/credit" rows that the user flagged
    const credits = pdfResult.transactions
      .map((t, i) => ({ tx: t, i }))
      .filter(({ i }) => creditRows.has(i));
    const toImport = [
      ...missing,
      ...credits.filter(({ tx }) => tx.status !== "missing"), // avoid duplicates
    ];
    if (toImport.length === 0) return;
    setImportingPdf(true);
    setPdfError(null);
    let ok = 0;
    for (const { tx, i } of toImport) {
      const response = await client.post(
        `/modules/cards/invoices/${invoiceId}/transactions`,
        { description: tx.description, amount: tx.amount, occurredAt: tx.date.slice(0, 10) }
      );
      if (response.ok) {
        ok += 1;
        if (particularRows.has(i) && particularPartnerId) {
          await client.post(`/modules/receivables`, {
            entityId,
            description: `Reembolso - ${tx.description}`,
            amount: tx.amount,
            dueDate: tx.date.slice(0, 10),
          });
        }
        // Payment rows (credit card bill payment): also create a pending bank movement (OUT)
        if (tx.lineCategory === "payment" && creditRows.has(i) && bankAccountId) {
          await client.post(`/modules/movements`, {
            bankAccountId,
            type: "OUT",
            origin: "CREDIT_CARD",
            operationNature: "FINANCE",
            description: tx.description,
            amount: Math.abs(tx.amount),
            occurredAt: tx.date.slice(0, 10),
          });
        }
      }
    }
    setImportingPdf(false);
    setParticularRows(new Set());
    setCreditRows(new Set());
    setPdfResult((prev) => {
      if (!prev) return prev;
      const creditIdxs = new Set(credits.map(({ i }) => i));
      return {
        ...prev,
        transactions: prev.transactions.map((t, i) =>
          t.status === "missing" || creditIdxs.has(i)
            ? { ...t, status: "matched" as const }
            : t
        ),
      };
    });
    await reload();
    if (ok < toImport.length) {
      setPdfError(`${toImport.length - ok} transação(ões) não puderam ser importadas.`);
    }
  };

  const handleCloseInvoice = async () => {
    const response = await client.post(`/modules/cards/invoices/${invoiceId}/close`, {});
    if (!response.ok) { setError("Falha ao fechar fatura."); return; }
    const payload = (await response.json()) as { data: InvoiceDetail };
    setInvoice((prev) => prev ? { ...prev, status: payload.data.status, totalAmount: payload.data.totalAmount } : prev);
    await reload();
  };

  const paginationText = useMemo(() => {
    if (total === 0) return "0 resultados";
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  const STATUS_PT: Record<string, { label: string; cls: string }> = {
    OPEN:   { label: "Aberta",   cls: "text-bg-warning" },
    CLOSED: { label: "Fechada",  cls: "text-bg-secondary" },
    PAID:   { label: "Paga",     cls: "text-bg-success" },
  };

  return (
    <div className="container py-4">
      <div className="mb-3">
        <Link to="/financeiro/faturas" className="btn btn-sm btn-outline-secondary mb-3">
          ← Voltar para faturas
        </Link>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <h1 className="mb-0">
            {invoice ? (
              <>
                {invoice.card.name}
                {invoice.card.last4 && (
                  <span className="text-muted ms-2 fs-5 fw-normal">···· {invoice.card.last4}</span>
                )}
              </>
            ) : (
              `Fatura ${invoiceId}`
            )}
          </h1>
          {invoice && (() => {
            const si = STATUS_PT[invoice.status] ?? { label: invoice.status, cls: "text-bg-light" };
            return <span className={`badge ${si.cls}`}>{si.label}</span>;
          })()}
        </div>
        {invoice && (
          <div className="d-flex gap-4 mt-2 text-muted small flex-wrap">
            <span>
              <strong>Competência:</strong>{" "}
              {String(invoice.competenceMonth).padStart(2, "0")}/{invoice.competenceYear}
            </span>
            <span>
              <strong>Fechamento:</strong> {formatDateBR(invoice.statementEnd)}
            </span>
            <span>
              <strong>Vencimento:</strong> {formatDateBR(invoice.dueDate)}
            </span>
            <span>
              <strong>Total:</strong> {formatMoneyBR(invoice.totalAmount)}
            </span>
          </div>
        )}
      </div>

      <div className="row g-4">
        {/* Transactions table */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-3 flex-wrap">
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={openModal}
                  >
                    + Nova transação
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleClearMetaAds}
                    disabled={clearingMeta}
                  >
                    {clearingMeta ? "Limpando..." : "Limpar Meta Ads"}
                  </button>
                </div>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleCloseInvoice}
                >
                  Fechar fatura
                </button>
              </div>
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <PageState loading={loading} error={listError}>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Descrição</th>
                        <th>Data</th>
                        <th>Valor</th>
                        <th>Parcela</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item) => (
                        <tr key={item.id}>
                          <td>
                            {item.description}
                            {item.origem === "META_ADS" && (
                              <span className="badge text-bg-info ms-1" style={{ fontSize: "0.65rem" }}>
                                CSV
                              </span>
                            )}
                          </td>
                          <td>{formatDateBR(item.occurredAt)}</td>
                          <td>{formatMoneyBR(item.amount)}</td>
                          <td>
                            {item.installmentNumber
                              ? `${item.installmentNumber}/${item.installmentCount}`
                              : "-"}
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-danger btn-sm py-0 px-1"
                              title="Excluir"
                              disabled={deletingId === item.id}
                              onClick={() => handleDeleteTransaction(item.id)}
                            >
                              &times;
                            </button>
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            Nenhuma transação.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PageState>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">{paginationText}</small>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page <= 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page * take >= total}
                    onClick={() => setPage(page + 1)}
                  >
                    Próximo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PDF reconciliation section */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => setShowPdfSection((v) => !v)}
              >
                <h5 className="card-title mb-0">Reconciliar com PDF da fatura</h5>
                <span className="text-muted small">{showPdfSection ? "▲ Ocultar" : "▼ Expandir"}</span>
              </div>
              {showPdfSection && (
                <div className="mt-3">
                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Conta bancária</label>
                      <select
                        className="form-select"
                        value={bankAccountId}
                        onChange={(e) => setBankAccountId(e.target.value === "" ? "" : Number(e.target.value))}
                      >
                        <option value="">Selecionar banco</option>
                        {bankAccounts.map((b) => (
                          <option key={b.id} value={b.id}>{b.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Sócio (transações particulares)</label>
                      <select
                        className="form-select"
                        value={particularPartnerId}
                        onChange={(e) => setParticularPartnerId(e.target.value === "" ? "" : Number(e.target.value))}
                      >
                        <option value="">Selecionar sócio</option>
                        {partners.map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row g-2 align-items-end mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Arquivo PDF</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".pdf,application/pdf"
                        onChange={(e) => {
                          setPdfFile(e.target.files?.[0] ?? null);
                          setPdfResult(null);
                          setParticularRows(new Set());
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Senha do PDF (opcional)</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ex: CPF sem pontos"
                        value={pdfPassword}
                        onChange={(e) => setPdfPassword(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleParsePdf}
                        disabled={loadingPdf || !pdfFile}
                      >
                        {loadingPdf ? "Processando..." : "Analisar PDF"}
                      </button>
                    </div>
                  </div>
                  {pdfError && <div className="alert alert-danger py-2">{pdfError}</div>}
                  {pdfResult && (
                    <>
                      <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                        <span className="badge text-bg-secondary">Total: {pdfResult.stats.total}</span>
                        <span className="badge text-bg-success">Conferidos: {pdfResult.stats.matched}</span>
                        {pdfResult.stats.missing > 0 && (
                          <span className="badge text-bg-warning text-dark">Faltando: {pdfResult.stats.missing}</span>
                        )}
                        {pdfResult.stats.moved > 0 && (
                          <span className="badge text-bg-primary">Movidos: {pdfResult.stats.moved}</span>
                        )}
                        {pdfResult.stats.otherInvoice > 0 && (
                          <span className="badge text-bg-danger">Em outra fatura: {pdfResult.stats.otherInvoice}</span>
                        )}
                        {pdfResult.stats.payment > 0 && (
                          <span className="badge text-bg-info text-dark">
                            Pagtos/Créditos: {pdfResult.stats.payment}
                          </span>
                        )}
                        {(pdfResult.stats.missing > 0 || creditRows.size > 0) && (
                          <button
                            type="button"
                            className="btn btn-warning btn-sm ms-2"
                            onClick={handleImportMissing}
                            disabled={importingPdf}
                          >
                            {importingPdf
                              ? "Importando..."
                              : `Importar ${pdfResult.stats.missing + creditRows.size} item(ns)`}
                          </button>
                        )}
                      </div>
                      {creditRows.size > 0 && (
                        <div className="alert alert-info py-2 mb-2 small">
                          <strong>{creditRows.size}</strong> crédito(s)/pagamento(s) selecionado(s) para abater o total da fatura.
                          {bankAccountId && pdfResult && pdfResult.transactions.some((tx, i) => creditRows.has(i) && tx.lineCategory === "payment") && (
                            <span className="ms-1">O(s) pagamento(s) de fatura também criarão um lançamento bancário (saída) na conta selecionada, pendente de conciliação.</span>
                          )}
                        </div>
                      )}
                      {particularRows.size > 0 && !particularPartnerId && (
                        <div className="alert alert-warning py-2 mb-2">
                          Selecione o sócio acima para gerar os lançamentos de reembolso das transações particulares.
                        </div>
                      )}
                      <div className="table-responsive">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th>Data</th>
                              <th>Descrição</th>
                              <th>Valor</th>
                              <th>Tipo</th>
                              <th>Status</th>
                              <th title="Marcar como despesa particular do sócio — gera contas a receber">Particular</th>
                              <th title="Incluir este crédito/pagamento para abater o total da fatura">Abater fatura</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pdfResult.transactions.map((tx, i) => {
                              const isParticular = particularRows.has(i);
                              const isCredit = creditRows.has(i);
                              const isNegative = tx.amount < 0;
                              const rowCls =
                                isCredit ? "table-success" :
                                isParticular ? "table-info" :
                                tx.status === "missing" ? "table-warning" :
                                tx.status === "moved" ? "table-primary" :
                                tx.status === "other_invoice" ? "table-danger" :
                                tx.status === "payment" ? "table-light" : "";
                              return (
                                <tr key={i} className={rowCls}>
                                  <td>{formatDateBR(tx.date)}</td>
                                  <td>
                                    {tx.description}
                                    {isNegative && (
                                      <span
                                        className="badge text-bg-success ms-1"
                                        style={{ fontSize: "0.65rem" }}
                                        title="Valor negativo — crédito ou pagamento antecipado"
                                      >
                                        crédito
                                      </span>
                                    )}
                                  </td>
                                  <td className={isNegative ? "text-success fw-semibold" : ""}>{formatMoneyBR(tx.amount)}</td>
                                  <td>
                                    <span className={`badge ${tx.type === "international" ? "text-bg-info" : "text-bg-light border"}`}>
                                      {tx.type === "international" ? "Internacional" : "Nacional"}
                                    </span>
                                  </td>
                                  <td>
                                    {tx.status === "matched" && (
                                      <span className="badge text-bg-success">Conferido</span>
                                    )}
                                    {tx.status === "missing" && !isParticular && (
                                      <span className="badge text-bg-warning text-dark">Faltando</span>
                                    )}
                                    {tx.status === "missing" && isParticular && (
                                      <span className="badge text-bg-info">Particular</span>
                                    )}
                                    {tx.status === "moved" && (
                                      <span className="badge text-bg-primary">
                                        Movida
                                        {tx.fromInvoice && (
                                          <span className="ms-1 fw-normal">
                                            de {String(tx.fromInvoice.month).padStart(2, "0")}/{tx.fromInvoice.year}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                    {tx.status === "other_invoice" && tx.otherInvoice && (
                                      <span className="badge text-bg-danger">
                                        Em {String(tx.otherInvoice.month).padStart(2, "0")}/{tx.otherInvoice.year}
                                      </span>
                                    )}
                                    {tx.status === "payment" && !isCredit && (
                                      <span className="badge text-bg-secondary">Pagto/Crédito</span>
                                    )}
                                    {tx.status === "payment" && isCredit && (
                                      <span className="badge text-bg-success">Abater fatura</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {tx.status === "missing" && (
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isParticular}
                                        onChange={() => toggleParticular(i)}
                                        title="Marcar como particular"
                                      />
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {(tx.status === "payment" || isNegative) && tx.status !== "matched" && (
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isCredit}
                                        onChange={() => toggleCredit(i)}
                                        title="Incluir este valor negativo para abater o total da fatura"
                                      />
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal — Nova transação */}
      {showModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={closeModal}
          />
          <div
            className="modal fade show d-block"
            style={{ zIndex: 1050 }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Nova transação</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    aria-label="Fechar"
                  />
                </div>
                <form onSubmit={handleCreate}>
                  <div className="modal-body">
                    {modalError && <div className="alert alert-danger py-2">{modalError}</div>}
                    <div className="row g-3">
                      <div className="col-md-8">
                        <label className="form-label">Descrição <span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Valor <span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          required
                          placeholder="0,00"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Data <span className="text-danger">*</span></label>
                        <input
                          className="form-control"
                          type="date"
                          value={occurredAt}
                          onChange={(e) => setOccurredAt(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Parcelas</label>
                        <input
                          className="form-control"
                          value={installmentCount}
                          onChange={(e) => setInstallmentCount(e.target.value)}
                          placeholder="Vazio = compra única"
                        />
                      </div>
                      <div className="col-md-4 d-flex align-items-end pb-1">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isRecurringModal"
                            checked={isRecurring}
                            onChange={(e) => setIsRecurring(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="isRecurringModal">
                            Recorrente mensal
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <AutocompleteInput
                          label="Categoria"
                          placeholder="Buscar categoria"
                          value={categoryOption?.id ?? null}
                          selectedLabel={categoryOption?.label}
                          fetchOptions={fetchCategories}
                          onSelect={setCategoryOption}
                        />
                      </div>
                      <div className="col-md-6">
                        <AutocompleteInput
                          label="Centro de custo"
                          placeholder="Buscar centro"
                          value={costCenterOption?.id ?? null}
                          selectedLabel={costCenterOption?.label}
                          fetchOptions={fetchCostCenters}
                          onSelect={setCostCenterOption}
                        />
                      </div>
                      <div className="col-md-6">
                        <AutocompleteInput
                          label="Conta contábil"
                          placeholder="Buscar conta"
                          value={accountOption?.id ?? null}
                          selectedLabel={accountOption?.label}
                          fetchOptions={fetchAccounts}
                          onSelect={setAccountOption}
                        />
                      </div>
                      <div className="col-md-6">
                        <AutocompleteInput
                          label="Fornecedor"
                          placeholder="Buscar fornecedor"
                          value={supplierOption?.id ?? null}
                          selectedLabel={supplierOption?.label}
                          fetchOptions={fetchSuppliers}
                          onSelect={setSupplierOption}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Salvando..." : canGenerateInstallments ? "Gerar parcelas" : "Adicionar"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
